import React, { useState } from "react";
import { ArrowUpRight, MapPin, Phone, Mail, Building2, Clock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import SocialBar from "../components/SocialBar";
import { toast } from "sonner";

const CONTACT_EMAIL = "hello@terwilliger.cz";

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("contact.validation_required"));
      return;
    }
    const subject = encodeURIComponent(
      form.subject.trim() || `New project inquiry — ${form.name}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-24 px-5 md:px-10">
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end">
          <div className="lg:col-span-9">
            <Reveal>
              <span className="tag">{t("contact.kicker")}</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="h-display mt-6 md:mt-8" data-testid="contact-title">
                {t("contact.title_a")}
                <span className="block ml-[5vw] md:ml-[8vw] text-[#E5FF00]">
                  {t("contact.title_b")}
                </span>
              </h1>
            </Reveal>
          </div>
          <div className="lg:col-span-3">
            <Reveal delay={0.2}>
              <p className="text-zinc-400 text-base md:text-lg leading-relaxed border-l border-white/15 pl-5 md:pl-6">
                {t("contact.lede")}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Form + info */}
      <section className="px-5 md:px-10 pb-20 md:pb-24" data-testid="contact-main">
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="space-y-2"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 lg:gap-x-10 gap-y-2">
                <div className="py-2">
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                    01 / {t("contact.form_name")} *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="input-brut"
                    type="text"
                    autoComplete="name"
                    data-testid="contact-input-name"
                    required
                  />
                </div>
                <div className="py-2">
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                    02 / {t("contact.form_email")} *
                  </label>
                  <input
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="input-brut"
                    type="email"
                    autoComplete="email"
                    data-testid="contact-input-email"
                    required
                  />
                </div>
              </div>

              <div className="py-2">
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  03 / {t("contact.form_subject")}
                </label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  className="input-brut"
                  type="text"
                  data-testid="contact-input-subject"
                />
              </div>

              <div className="py-2">
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  04 / {t("contact.form_message")} *
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  className="input-brut"
                  data-testid="contact-input-message"
                  rows={6}
                  required
                />
              </div>

              <div className="pt-8 flex flex-col sm:flex-row sm:items-center gap-6">
                <button type="submit" className="btn-acid" data-testid="contact-submit-btn">
                  {t("contact.form_send")}
                  <ArrowUpRight size={16} strokeWidth={2.5} />
                </button>
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 max-w-xs">
                  {t("contact.form_note")}
                </p>
              </div>
            </form>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="border border-white/10 bg-[#0a0a0a]">
              <div className="p-8 md:p-10 border-b border-white/10">
                <span className="tag">{t("contact.info_kicker")}</span>
                <h3 className="font-display text-3xl md:text-4xl mt-5 tracking-tight">
                  Praha, CZ
                </h3>
                <div className="mt-6">
                  <SocialBar size="lg" />
                </div>
              </div>

              <div className="divide-y divide-white/10">
                <div className="p-6 md:p-8 flex items-start gap-4">
                  <Phone size={18} className="text-[#E5FF00] mt-1 shrink-0" />
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {t("contact.info_phone_label")}
                    </p>
                    <a
                      href="tel:+420731385947"
                      className="font-display text-xl md:text-2xl mt-1 inline-block py-2 hover:text-[#E5FF00] transition-colors"
                      data-testid="contact-info-phone"
                    >
                      +420 731 385 947
                    </a>
                  </div>
                </div>

                <div className="p-6 md:p-8 flex items-start gap-4">
                  <Mail size={18} className="text-[#E5FF00] mt-1 shrink-0" />
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {t("contact.info_email_label")}
                    </p>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      className="font-display text-xl md:text-2xl mt-1 inline-block py-2 hover:text-[#E5FF00] transition-colors break-all"
                      data-testid="contact-info-email"
                    >
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </div>

                <div className="p-6 md:p-8 flex items-start gap-4">
                  <MapPin size={18} className="text-[#E5FF00] mt-1 shrink-0" />
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {t("contact.info_address_label")}
                    </p>
                    <p className="font-display text-lg md:text-xl mt-2 leading-snug">
                      Děčínská 552/1, Střížkov
                      <br />
                      180 00 Praha 8
                    </p>
                  </div>
                </div>

                <div className="p-6 md:p-8 flex items-start gap-4">
                  <Building2 size={18} className="text-[#E5FF00] mt-1 shrink-0" />
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {t("contact.info_id_label")}
                    </p>
                    <p className="font-display text-lg md:text-xl mt-2">
                      22293191
                    </p>
                  </div>
                </div>

                <div className="p-6 md:p-8 flex items-start gap-4">
                  <Clock size={18} className="text-[#E5FF00] mt-1 shrink-0" />
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {t("contact.info_hours_label")}
                    </p>
                    <p className="font-display text-lg md:text-xl mt-2">
                      {t("contact.info_hours_value")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="px-5 md:px-10 pb-20 md:pb-24" data-testid="contact-map-section">
        <div className="max-w-[1480px] mx-auto">
          <div className="relative aspect-[4/3] sm:aspect-[16/9] md:aspect-[16/6] border border-white/10 overflow-hidden">
            <iframe
              title="Terwilliger s.r.o. — Děčínská 552/1, Praha 8"
              src="https://www.openstreetmap.org/export/embed.html?bbox=14.473%2C50.115%2C14.493%2C50.125&layer=mapnik&marker=50.12000%2C14.48300"
              className="absolute inset-0 w-full h-full grayscale contrast-125"
              loading="lazy"
              data-testid="contact-map-iframe"
            />
            <div className="absolute top-3 left-3 md:top-4 md:left-4 bg-black/80 border border-white/10 px-3 py-2 md:px-4 md:py-3 backdrop-blur">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Studio
              </p>
              <p className="font-display text-sm md:text-base mt-1">Praha 8 · Střížkov</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
